import { Component, NgModule } from '@angular/core';
import { RouteReuseStrategy, Router, RouterModule, Routes } from '@angular/router';
import { DefaultUrls } from 'src/assets/default.urls';
import { CustomRouteReuseStrategy } from './custom-route-reuse-strategy';
import { AdminAccessGuard } from './guards/admin-access/admin-access.guard';
import { DeviceAddressRedirectionGuard } from './guards/device-address-redirection/device-address-redirection.guard';
import { DeviceDetailDialogOpenGuard } from './guards/device-detail-dialog-open/device-detail-dialog-open.guard';
import { EditorAccessGuard } from './guards/editor-access/editor-access.guard';
import { GuestGuard } from './guards/guest.guard';
import { TeamDetailGuard } from './guards/team-detail/team-detail.guard';
import { TeamManagementAccessGuard } from './guards/team-management-page/team-management-access.guard';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { TeamsResolverService } from './services/resolvers/team-list/team-list-resolver.service';
import { UserListResolverService } from './services/resolvers/user-list/user-list-resolver.service';
import { AuthGuard } from './utility/authGuard';
import { DashboardGuard } from './utility/dashboard-guard';

const routes: Routes = [
  {
    path: DefaultUrls.routePath.empty,
    children: [
      {
        // add the ai disclaimer guard to the root of the app
        path: DefaultUrls.routePath.aiDisclaimer,
        canActivate: [GuestGuard],
        loadChildren: () => import('./pages/ai-prototype-disclaimer-approval/ai-prototype-disclaimer-approval.module').then(m => m.AiPrototypeDisclaimerApprovalModule)
      },
      {
        path: DefaultUrls.routePath.thingies,
        canActivate: [AuthGuard],
        children: [
          {
            path: DefaultUrls.routePath.empty,
            loadChildren: () => import('./pages/dashboard/dashboard-routing.module').then(m => m.DashboardRoutingModule)
          }, {
            path: DefaultUrls.routePath.thingieDetail,
            loadChildren: () => import('./pages/thingie-detail/thingie-detail/thingie-detail-routing.module').then(m => m.ThingieDetailRoutingModule)
          },
          {
            path: DefaultUrls.routePath.create,
            loadChildren: () => import('./pages/thingie-creation/thingie-creation.page.module').then(m => m.ThingieCreationModule),
            canActivate: [EditorAccessGuard],
            canLoad: [EditorAccessGuard]
          },
          {
            path: DefaultUrls.routePath.thingieFakeData,
            loadChildren: () => import('./pages/thingie-detail/fakedata/thingie-fakedata.page.module').then(m => m.ThingieFakeDataModule)
          },
        ]
      },
      {
        path: DefaultUrls.routePath.devices,
        canActivate: [AuthGuard],
        children: [
          {
            path: DefaultUrls.routePath.empty,
            loadChildren: () => import('./pages/devices/devices-routing.module').then(m => m.DevicesRoutingModule)
          },
          {
            path: DefaultUrls.routePath.deviceDetail,
            canLoad: [DeviceDetailDialogOpenGuard],
            loadChildren: () => import('./pages/devices/devices-routing.module').then(m => m.DevicesRoutingModule)
          },
          // For special resolve-redirection-purposes
          // from a device address to the device page
          {
            path: DefaultUrls.routePath.deviceAddress,
            canActivate: [DeviceAddressRedirectionGuard],
            // Has to be provided, because 'redirectTo' will ovrride our URLTree
            component: (class extends Component {})
          },
          {
            path: DefaultUrls.routePath.create,
            loadChildren: () => import('./pages/device-creation/device-creation.page.module').then(m => m.DeviceCreationModule),
            canActivate: [AdminAccessGuard],
            canLoad: [AdminAccessGuard]
          },
        ]
      },
      {
        path: DefaultUrls.routePath.devicesDebug,
        canActivate: [AuthGuard],
        children: [
          {
            path: DefaultUrls.routePath.deviceDebugDetail,
            loadChildren: () => import('./pages/device-detail/device-detail-routing.module').then(m => m.DeviceDetailRoutingModule)
          },
        ]
      },

      /* {
        path: 'user-center',
        loadChildren: () => import('./pages/user-center/user-center.module').then(m => m.UserCenterModule)
      }, */
      {
        path: DefaultUrls.routePath.vendorTooling,
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/vendor-tooling/vendor-tooling-routing.module').then(m => m.VendorToolingRoutingModule)
      },
      {
        path: DefaultUrls.routePath.error,
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/404/page-not-found/page-not-found.module').then(m => m.PageNotFoundModule)
      },
      {
        path: DefaultUrls.routePath.swagger, redirectTo: DefaultUrls.routePath.swaggerSlash, pathMatch: 'full', title: 'API'
      },
      {
        path: DefaultUrls.routePath.swaggerService,
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/swagger/swagger.module').then(m => m.SwaggerModule)
      },
      {
        path: DefaultUrls.routePath.dashboard,
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/dashboard/dashboard-routing.module').then(m => m.DashboardRoutingModule)
      },
      {
        path: DefaultUrls.routePath.admin,
        canActivate: [AuthGuard, AdminAccessGuard],
        canLoad: [AdminAccessGuard],
        loadChildren: () => import('./pages/admin-page/admin-page.module').then(m => m.AdminPageModule)
      },
      {
        path: DefaultUrls.routePath.teamDetail,
        canActivate: [AuthGuard, TeamDetailGuard],
        resolve: {
          userList: UserListResolverService
        },
        loadChildren: () => import('./pages/team-detail/team-detail.module').then(m => m.TeamDetailModule)
      },
      {
        path: DefaultUrls.routePath.teamManagement,
        canActivate: [AuthGuard, TeamManagementAccessGuard],
        resolve: {
          teams: TeamsResolverService
        },
        loadChildren: () => import('./pages/team-management-page/team-management-page.module').then(m => m.TeamManagementPageModule)
      },
      {
        path: DefaultUrls.routePath.empty,
        canActivate: [AuthGuard, DashboardGuard()],
        component: DashboardComponent,
        title: 'Dashboard'
      },
      {
        path: DefaultUrls.routePath.dotsCalibration,
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/dots-calibration/dots-calibration.module').then(m => m.DotsCalibrationModule)
      },
      {
        canActivate: [AuthGuard],
        path: DefaultUrls.routePath.wildcard,
        loadChildren: () => import('./pages/404/page-not-found/page-not-found.module').then(m => m.PageNotFoundModule)
      }
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }
  ]
})
export class AppRoutingModule {
  constructor(router: Router) {
    router.onSameUrlNavigation = 'reload';
  }
}
